import { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Grid,
    Typography
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useAppDispatch } from '../../../hooks/reduxHooks';
import { showAlert } from '../../../features/slices/applicationSlice';
import { ALERT_CONFIRM_TEXT } from '../../../constants/values';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { DrawerHeaderWithLeftIcon } from '../../../components/Drawer/DrawerHeader';
import BackdropComponent from '../../../components/Backdrop/BackdropComponent';
import ButtonActions from '../../../components/ButtonActions';
import AutocompleteComponent from '../../../components/layout/Autocomplete';
import useContracts from '../../../components/layout/ContractSelector/hook/useContracts';
import {
    useLazyGetCarcinogenicPendingByCuitQuery,
    useLazyGetCarcinogenicSrtValidacionesLoteQuery,
    useVincularCarcinogenicSRTByCuitMutation
} from '../../../services/carcinogenicApi';
import { handleErrors } from '../../../utils/common';
import HallazgosEstablecimiento, { ErrorSvcc } from './HallazgosEstablecimiento';

interface SVCCVincularDrawerProps {
    handleCloseDrawer: () => void;
    translate: (key: string) => string;
    cuitPreseleccionado?: string;
}

interface ValidacionInforme {
    completo: boolean;
    errores: ErrorSvcc[];
}

interface Bloqueado {
    idInforme: number;
    errores: ErrorSvcc[];
}

/**
 * El endpoint batch de validaciones y el de vincular rechazan (400) ids
 * duplicados o no positivos (`@ArrayUnique`, `@IsPositive`). Los informes
 * pendientes no deberían traer nada así, pero se sanea antes de mandar.
 */
const idsUnicosPositivos = (ids: number[]): number[] =>
    Array.from(new Set(ids)).filter((id) => Number.isInteger(id) && id > 0);

const SVCCVincularDrawer = ({
    handleCloseDrawer,
    translate,
    cuitPreseleccionado
}: SVCCVincularDrawerProps) => {
    const dispatch = useAppDispatch();
    const [selectedContract, setSelectedContract] = useState<any>(null);
    const [establishments, setEstablishments] = useState<any[]>([]);
    const [fetchError, setFetchError] = useState(false);
    const [canVincular, setCanVincular] = useState(false);
    const [previewFetched, setPreviewFetched] = useState(false);
    const [validaciones, setValidaciones] = useState<Record<number, ValidacionInforme>>({});
    const [bloqueados, setBloqueados] = useState<Bloqueado[]>([]);
    const [validacionesFallidas, setValidacionesFallidas] = useState(false);
    const [selectedInformes, setSelectedInformes] = useState<Set<number>>(new Set());

    const { rawContracts, isFetching: isFetchingContracts } = useContracts();

    const contractOptions = rawContracts.map((c: any) => ({
        id: c.id,
        nombre: `${c.cuit} - ${c.nombre}`,
        cuit: c.cuit
    }));

    const [getPendingEstablishments, { isFetching: isFetchingEstablishments }] =
        useLazyGetCarcinogenicPendingByCuitQuery();

    /*
     * Lazy query y no mutation: la validación no muta nada y ahora está
     * cacheada, así que revisar dos veces el mismo CUIT reusa la respuesta en
     * vez de revalidar. `trigger(args, true)` prefiere el valor cacheado.
     */
    const [validarLote, { isFetching: isFetchingValidaciones }] =
        useLazyGetCarcinogenicSrtValidacionesLoteQuery();

    const [vincularSRT, { isLoading: isVinculating }] = useVincularCarcinogenicSRTByCuitMutation();

    const cuitActivo = cuitPreseleccionado ?? selectedContract?.cuit;

    /**
     * Valida todos los informes del CUIT en una sola llamada. Una request por
     * informe no escala: hay contratos con cientos de establecimientos.
     *
     * Si la llamada falla (red, 5xx), no se bloquea de este lado: el backend
     * rechaza el envío igual si algún establecimiento tiene un bloqueante.
     * Pero el fallo se marca en `validacionesFallidas` para que la UI avise
     * en vez de mostrarse idéntica a "todo está limpio".
     */
    const validarInformes = async (
        idInformes: number[]
    ): Promise<Record<number, ValidacionInforme>> => {
        if (!idInformes.length) return {};
        try {
            const res = await validarLote(idInformes, true).unwrap();
            setValidacionesFallidas(false);
            return (res?.data ?? res ?? {}) as Record<number, ValidacionInforme>;
        } catch {
            setValidacionesFallidas(true);
            return {};
        }
    };

    const handlePreview = async (cuit?: string) => {
        const cuitAUsar = cuit ?? cuitActivo;
        if (!cuitAUsar) return;
        setFetchError(false);
        setValidaciones({});
        setBloqueados([]);
        setValidacionesFallidas(false);
        try {
            const result = await getPendingEstablishments(cuitAUsar).unwrap();
            const data = result?.data?.data ?? result?.data ?? result ?? [];
            const list = Array.isArray(data) ? data : [];
            setEstablishments(list);
            setPreviewFetched(true);
            setCanVincular(list.length > 0);

            const idInformes = idsUnicosPositivos(list.map((e: any) => e.idInforme).filter(Boolean));
            const validacionesPorInforme = await validarInformes(idInformes);
            setValidaciones(validacionesPorInforme);
            // Arranca todo tildado salvo lo que la validacion previa bloquea:
            // el caso normal es mandar el CUIT completo, y destildar es la
            // excepcion. Se calcula sobre el resultado local porque el estado
            // de validaciones todavia no se aplico en este render.
            setSelectedInformes(
                new Set(
                    idInformes.filter(
                        (id) => validacionesPorInforme[id]?.completo !== false
                    )
                )
            );
        } catch {
            setFetchError(true);
        }
    };

    const tieneErroresBloqueantes = (idInforme: number) =>
        validaciones[idInforme]?.completo === false;

    useEffect(() => {
        if (cuitPreseleccionado) {
            handlePreview(cuitPreseleccionado);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cuitPreseleccionado]);

    const handleContractChange = (value: any) => {
        setSelectedContract(value || null);
        setCanVincular(false);
        setEstablishments([]);
        setSelectedInformes(new Set());
        setFetchError(false);
        setPreviewFetched(false);
        setValidaciones({});
        setBloqueados([]);
        setValidacionesFallidas(false);
    };

    /**
     * Sólo se puede tildar lo que la validación pre-envío deja pasar: un
     * establecimiento con bloqueantes va a rebotar en la SRT igual, así que no
     * se ofrece mandarlo. Si la validación no pudo correr
     * (`validacionesFallidas`), no se bloquea nada — no sabemos si está mal, y
     * el backend rechaza el envío de todos modos si lo está.
     */
    const esSeleccionable = (idInforme: number) => !tieneErroresBloqueantes(idInforme);

    const informesSeleccionables = establishments
        .map((e: any) => e.idInforme)
        .filter((id: number) => Boolean(id) && esSeleccionable(id));

    const toggleInforme = (id: number) => {
        if (!esSeleccionable(id)) return;
        setSelectedInformes((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const allSelected =
        informesSeleccionables.length > 0 &&
        selectedInformes.size === informesSeleccionables.length;
    const someSelected = selectedInformes.size > 0 && !allSelected;

    const toggleAll = () => {
        setSelectedInformes(allSelected ? new Set() : new Set(informesSeleccionables));
    };

    const cantidadBloqueados = establishments.filter((e: any) =>
        tieneErroresBloqueantes(e.idInforme)
    ).length;

    const handleVincular = async () => {
        if (!cuitActivo) return;
        setBloqueados([]);
        const idInformes = idsUnicosPositivos(Array.from(selectedInformes));
        if (!idInformes.length) return;
        try {
            await vincularSRT({ cuit: cuitActivo, idInformes }).unwrap();
            // Mensaje deliberadamente neutro: el backend puede devolver 200 sin
            // haber enviado nada nuevo (idempotencia — CUIT ya vinculado con el
            // mismo lote en paso >= 4). No podemos distinguir ese caso de un envío
            // real desde la respuesta, así que no afirmamos éxito.
            enqueueSnackbar('Solicitud procesada. Revisá el estado en la grilla.', {
                variant: 'info'
            });
            handleCloseDrawer();
        } catch (error: any) {
            // El backend responde 400 con los establecimientos que bloquean y
            // no manda nada a la SRT. `.unwrap()` tira, así que el motivo se
            // lee acá (catch), nunca de un campo `.error` del response que
            // nunca llega a ejecutarse.
            //
            // Este 400 en particular (`BadRequestException({ bloqueados })`)
            // no trae `message` propio: Nest completa `error.data.message`
            // con el nombre de la clase ("Bad Request Exception"), que no le
            // sirve al usuario. Por eso se corta aquí con un mensaje propio
            // en vez de caer a `handleErrors`, que mostraría ese texto crudo.
            const detalle = error?.data?.bloqueados;
            if (Array.isArray(detalle) && detalle.length) {
                setBloqueados(detalle);
                dispatch(
                    showAlert({
                        title: 'No se pudo enviar la presentación a la SRT',
                        message: 'No se envió nada. Corregí los establecimientos señalados y volvé a intentar.',
                        icon: 'error',
                        confirmText: ALERT_CONFIRM_TEXT
                    })
                );
                return;
            }
            handleErrors(error);
        }
    };

    return (
        <Box
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            sx={{ height: '100%', minHeight: '100%' }}
        >
            <Grid p={1} sx={{ flex: 1, overflowY: 'auto' }}>
                <DrawerHeaderWithLeftIcon
                    onIconClick={handleCloseDrawer}
                    text="Vinculación SRT"
                    subtitle="Vincular con la SRT todos los establecimientos (informes) pertenecientes a un contrato"
                    LeftIcon={<LinkOutlinedIcon sx={{ fontSize: 20, color: '#15803d' }} />}
                />

                {!cuitPreseleccionado && (
                    <>
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1, color: '#475569', pl: '.3rem' }}>
                                Por favor seleccione un contrato
                            </Typography>
                            <AutocompleteComponent
                                name="contrato"
                                label="Contrato"
                                value={selectedContract}
                                options={contractOptions}
                                templateLabel={(option: any) => option?.nombre || ''}
                                onChange={handleContractChange}
                                fullWidth
                                loading={isFetchingContracts}
                            />
                        </Box>

                        <Box display="flex" justifyContent="flex-end" sx={{ mt: 1.5 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                disabled={!selectedContract || isFetchingEstablishments}
                                onClick={() => handlePreview()}
                            >
                                Previsualizar establecimientos afectados
                            </Button>
                        </Box>
                    </>
                )}

                {isFetchingEstablishments && (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        sx={{ mt: 3, minHeight: 80 }}
                    >
                        <CircularProgress size={28} />
                    </Box>
                )}

                {!isFetchingEstablishments && fetchError && (
                    <Box
                        display="flex"
                        alignItems="flex-start"
                        gap={1}
                        sx={{
                            mt: 2,
                            p: 1.5,
                            bgcolor: '#FEE2E2',
                            borderRadius: 1,
                            border: '1px solid #FCA5A5'
                        }}
                    >
                        <InfoOutlinedIcon
                            sx={{ color: '#DC2626', fontSize: 16, mt: 0.2, flexShrink: 0 }}
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ color: '#DC2626' }}>
                                {canVincular
                                    ? 'No se pudieron actualizar los establecimientos relacionados, puede intentar vincular de igual manera presionando el botón "Iniciar vinculación"'
                                    : 'No se pudieron obtener los establecimientos relacionados.'}
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                color="error"
                                sx={{ alignSelf: 'flex-start' }}
                                onClick={() => handlePreview()}
                            >
                                Reintentar
                            </Button>
                        </Box>
                    </Box>
                )}

                {!isFetchingEstablishments && !fetchError && establishments.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Typography
                            variant="body2"
                            sx={{ mb: 1, color: '#475569', fontWeight: 500 }}
                        >
                            {`${establishments.length} establecimiento(s) afectado(s)`}
                        </Typography>

                        <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#475569' }}>
                            Se envían todos los establecimientos del CUIT. La SRT no admite
                            presentaciones parciales.
                        </Typography>

                        {isFetchingValidaciones && (
                            <Typography variant="caption" sx={{ color: '#64748B' }}>
                                Validando datos contra las reglas de la SRT…
                            </Typography>
                        )}

                        {!isFetchingValidaciones && validacionesFallidas && (
                            <Typography
                                variant="caption"
                                sx={{ display: 'block', mb: 1, color: '#B45309' }}
                            >
                                No se pudieron validar los establecimientos contra las reglas de
                                la SRT. Podés continuar, pero revisá los datos antes de vincular.
                            </Typography>
                        )}

                        {!isFetchingValidaciones && cantidadBloqueados > 0 && (
                            <Box
                                display="flex"
                                alignItems="flex-start"
                                gap={1}
                                sx={{
                                    mb: 1,
                                    p: 1.5,
                                    bgcolor: '#FEF2F2',
                                    borderRadius: 1,
                                    border: '1px solid #FCA5A5'
                                }}
                            >
                                <InfoOutlinedIcon
                                    sx={{ color: '#DC2626', fontSize: 16, mt: 0.2, flexShrink: 0 }}
                                />
                                <Typography variant="body2" sx={{ color: '#DC2626' }}>
                                    {`${cantidadBloqueados} establecimiento(s) quedaron destildados: la SRT los rechazaría. Podés vincular el resto ahora, o corregir lo indicado en cada uno y volver a previsualizar para incluirlos.`}
                                </Typography>
                            </Box>
                        )}
                        <Box
                            sx={{
                                border: '1px solid #e2e8f0',
                                borderRadius: 1,
                                overflow: 'hidden'
                            }}
                        >
                            <Box
                                display="grid"
                                gridTemplateColumns="36px 64px 1fr 72px"
                                sx={{
                                    bgcolor: '#f1f5f9',
                                    borderBottom: '1px solid #e2e8f0',
                                    pl: 0,
                                    pr: 1.5,
                                    py: 0.25,
                                    alignItems: 'center'
                                }}
                            >
                                <Checkbox
                                    size="small"
                                    checked={allSelected}
                                    indeterminate={someSelected}
                                    disabled={informesSeleccionables.length === 0}
                                    onChange={toggleAll}
                                    inputProps={{
                                        'aria-label': 'Seleccionar todos los establecimientos'
                                    }}
                                />
                                <Typography variant="caption" fontWeight={700} color="#475569">
                                    ID
                                </Typography>
                                <Typography variant="caption" fontWeight={700} color="#475569">
                                    Nombre
                                </Typography>
                                <Typography
                                    variant="caption"
                                    fontWeight={700}
                                    color="#475569"
                                    textAlign="center"
                                >
                                    Informe
                                </Typography>
                            </Box>
                            <Box sx={{ maxHeight: 480, overflowY: 'auto', bgcolor: '#f8fafc' }}>
                                {establishments.map((est: any, index: number) => {
                                    const bloqueadoPorBackend = bloqueados.find(
                                        (b) => b.idInforme === est.idInforme
                                    );
                                    const erroresPreEnvio =
                                        validaciones[est.idInforme]?.errores ?? [];
                                    const hallazgos = bloqueadoPorBackend
                                        ? bloqueadoPorBackend.errores
                                        : erroresPreEnvio;
                                    const seleccionable = esSeleccionable(est.idInforme);
                                    const tildado = selectedInformes.has(est.idInforme);

                                    return (
                                        <Box
                                            key={est.idInforme ?? index}
                                            sx={{
                                                borderBottom:
                                                    index < establishments.length - 1
                                                        ? '1px solid #e2e8f0'
                                                        : 'none'
                                            }}
                                        >
                                            <Box
                                                display="grid"
                                                gridTemplateColumns="36px 64px 1fr 72px"
                                                sx={{
                                                    pl: 0,
                                                    pr: 1.5,
                                                    py: 0.25,
                                                    alignItems: 'center',
                                                    '&:hover': { bgcolor: '#f0f4f8' },
                                                    opacity: tildado ? 1 : 0.45
                                                }}
                                            >
                                                <Checkbox
                                                    size="small"
                                                    checked={tildado}
                                                    disabled={!seleccionable}
                                                    onChange={() => toggleInforme(est.idInforme)}
                                                    inputProps={{
                                                        'aria-label': `Seleccionar informe ${est.idInforme}`
                                                    }}
                                                />
                                                <Typography variant="body2">
                                                    {est.idEstablecimiento}
                                                </Typography>
                                                <Typography variant="body2">
                                                    {est.nombreEstablecimiento}
                                                </Typography>
                                                <Typography variant="body2" textAlign="center">
                                                    {est.idInforme}
                                                </Typography>
                                            </Box>

                                            {hallazgos.length > 0 && (
                                                <Box sx={{ px: 1.5, pb: 1 }}>
                                                    <HallazgosEstablecimiento errores={hallazgos} />
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Box>
                )}

                {!isFetchingEstablishments && !fetchError && previewFetched && establishments.length === 0 && (
                    <Box
                        sx={{
                            mt: 2,
                            p: 1.5,
                            bgcolor: '#F8FAFC',
                            borderRadius: 1,
                            border: '1px solid #e2e8f0'
                        }}
                    >
                        <Typography variant="body2" sx={{ color: '#64748B' }}>
                            No se encontraron establecimientos pendientes para este CUIT.
                        </Typography>
                    </Box>
                )}

                {bloqueados.length > 0 && (
                    <Box
                        sx={{
                            mt: 2,
                            p: 1.5,
                            bgcolor: '#FFF9F8',
                            borderRadius: 1,
                            border: '1px solid #F2D6D3'
                        }}
                    >
                        <Typography variant="body2" sx={{ color: '#B3261E', fontWeight: 600 }}>
                            No se envió nada. Corregí estos establecimientos y volvé a intentar.
                        </Typography>
                        {/*
                         * Sólo el listado de a quiénes afecta: el detalle de cada
                         * hallazgo ya se muestra inline en la fila del
                         * establecimiento, y repetirlo acá duplicaba el mismo
                         * texto dos veces en pantalla.
                         */}
                        {bloqueados.map((b) => {
                            const establecimiento = establishments.find(
                                (e: any) => e.idInforme === b.idInforme
                            );
                            const etiqueta = establecimiento?.nombreEstablecimiento
                                ? `${establecimiento.nombreEstablecimiento} (Informe ${b.idInforme})`
                                : `Informe ${b.idInforme}`;

                            return (
                                <Typography
                                    key={b.idInforme}
                                    variant="body2"
                                    sx={{ color: '#B3261E', fontWeight: 700, mt: 1 }}
                                >
                                    {etiqueta}
                                </Typography>
                            );
                        })}
                    </Box>
                )}
            </Grid>

            <Grid container direction="row" alignItems="center" sx={{ marginTop: '1rem' }}>
                <Box
                    sx={{
                        '& > :not(style)': { m: 1 },
                        display: 'flex',
                        flex: 1,
                        justifyContent: 'flex-end',
                        alignItems: 'center'
                    }}
                >
                    <ButtonActions
                        handleClose={handleCloseDrawer}
                        renderBackAction
                        returnText="Cancelar"
                        confirmText="Iniciar vinculación"
                        loading={isVinculating}
                        disabled={
                            !canVincular ||
                            selectedInformes.size === 0 ||
                            isFetchingValidaciones ||
                            isVinculating
                        }
                        onClick={handleVincular}
                        borderRadius="4px"
                        size="small"
                    />
                </Box>
            </Grid>

            <BackdropComponent loading={isVinculating} />
        </Box>
    );
};

export { SVCCVincularDrawer };
